/**
 * useBridgeMode — persisted BRIDGE / COMPACT mode state for the Fleet HUD
 * grand bridge (Wave 2, Item 1).
 *
 * CONSUMERS:
 * - frontend/src/pages/FleetHud.tsx: decides BridgeShell vs wave-1 AppLayout
 *
 * STORAGE CONTRACT (memory/feedback_no_localstorage_mirror.md — hard lesson):
 * - localStorage is read ONLY inside the lazy useState initializers (mount).
 * - localStorage is written ONLY inside the explicit toggle callbacks.
 * - NOTHING here writes on render — no mirroring effects, no render-loop
 *   persistence. Render is pure with respect to storage.
 *
 * SSR-safe: every window/localStorage access is guarded.
 */
import { useCallback, useState, useSyncExternalStore } from 'react';

export const BRIDGE_MODE_KEY = 'claimgenius.bridge.mode';
export const BRIDGE_COMPACT_KEY = 'claimgenius.bridge.compact';

/** Tailwind `lg` breakpoint — bridge is a desktop-only frame. */
const LG_QUERY = '(min-width: 1024px)';

function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // storage disabled / private mode — fall through to defaults
  }
}

function writeStored(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Quota / privacy-mode rejection: the in-memory toggle still works.
  }
}

function getIsLg(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') return window.matchMedia(LG_QUERY).matches;
  return window.innerWidth >= 1024;
}

function subscribeLg(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const mql = window.matchMedia(LG_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

export interface BridgeModeState {
  /**
   * True when the bridge should actually render: persisted preference ON
   * *and* viewport ≥ lg. Below lg the wave-1 stacked layout renders even if
   * the stored preference is 'true'.
   */
  bridgeMode: boolean;
  /** Flip the persisted bridge preference (the '⌂ CONSOLE' exit / re-entry). */
  toggleBridgeMode: () => void;
  /** COMPACT sub-mode within bridge (stacked variant). Persisted. */
  isCompact: boolean;
  /** Flip the persisted compact sub-mode (BRIDGE ↔ COMPACT toggle). */
  toggleCompact: () => void;
}

export function useBridgeMode(): BridgeModeState {
  // Live viewport tracking — a stored 'true' must not render the bridge on a
  // narrow window, and resizing across lg must re-slot without a reload.
  const isLg = useSyncExternalStore(subscribeLg, getIsLg, () => false);

  // Lazy initial state: the ONLY localStorage read in this hook (mount only).
  const [pref, setPref] = useState<boolean>(() => {
    const stored = readStored(BRIDGE_MODE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return getIsLg(); // default: bridge on at lg+, off below
  });
  const [isCompact, setIsCompact] = useState<boolean>(
    () => readStored(BRIDGE_COMPACT_KEY) === 'true'
  );

  const toggleBridgeMode = useCallback(() => {
    setPref((prev) => {
      const next = !prev;
      writeStored(BRIDGE_MODE_KEY, String(next)); // the ONLY mode write
      return next;
    });
  }, []);

  const toggleCompact = useCallback(() => {
    setIsCompact((prev) => {
      const next = !prev;
      writeStored(BRIDGE_COMPACT_KEY, String(next)); // the ONLY compact write
      return next;
    });
  }, []);

  return { bridgeMode: pref && isLg, toggleBridgeMode, isCompact, toggleCompact };
}
